import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  RadioGroup,
  Radio,
  InputAdornment,
  Chip,
  Tooltip,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Close,
  OpenInFull,
  StarBorder,
  AddPhotoAlternate,
  HelpOutline,
  ExpandMore,
} from '@mui/icons-material';
import { productAPI } from '../../services/api';
import { toast } from 'react-toastify';

/**
 * ProductSelect - Reusable product autocomplete with "Add new" functionality
 * 
 * Props:
 * - value: product object or product ID
 * - onChange: callback with selected product object
 * - products: optional pre-loaded products array
 * - onProductsChange: callback when products list changes
 * - onProductSelect: callback when product is selected (receives full product object)
 */
const ProductSelect = ({ 
  value, 
  onChange, 
  products: externalProducts,
  onProductsChange,
  onProductSelect,
}) => {
  const [products, setProducts] = useState(externalProducts || []);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productTab, setProductTab] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    productType: 'goods',
    canBeSold: true,
    canBePurchased: true,
    isRental: false,
    trackInventory: false,
    invoicingPolicy: 'ordered',
    salesPrice: '',
    salesTax: '5',
    cost: '',
    purchaseTax: '5',
    category: '',
    reference: '',
    barcode: '',
    hsnCode: '',
    company: 'all',
    internalNotes: '',
  });

  // Helper function to clean product names (remove newlines and extra spaces)
  const cleanProductName = (name) => {
    if (!name) return '';
    return name
      .replace(/[\r\n]+/g, ' ') // Replace newlines with space
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim(); // Remove leading/trailing spaces
  };

  useEffect(() => {
    if (!externalProducts) {
      fetchProducts();
    }
  }, [externalProducts]);

  useEffect(() => {
    if (externalProducts) {
      setProducts(externalProducts);
    }
  }, [externalProducts]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getAllForDropdown();
      setProducts(response.data || []);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.name.trim()) {
      toast.error('Please enter a product name');
      return;
    }
    
    try {
      const response = await productAPI.create({
        name: newProduct.name,
        productType: newProduct.productType,
        unitPrice: parseFloat(newProduct.salesPrice) || 0,
        taxRate: parseFloat(newProduct.salesTax) || 18,
        category: newProduct.category || '',
        sku: newProduct.reference || '',
        hsnCode: newProduct.hsnCode || '',
        description: newProduct.internalNotes || '',
        canBeSold: newProduct.canBeSold,
        canBePurchased: newProduct.canBePurchased,
        trackInventory: newProduct.trackInventory,
      });
      
      const createdProduct = response.data.product;
      const updatedProducts = [...products, createdProduct];
      setProducts(updatedProducts);
      
      if (onProductsChange) {
        onProductsChange(updatedProducts);
      }
      
      if (onProductSelect) {
        onProductSelect(createdProduct);
      }
      
      onChange(createdProduct._id);
      handleCloseDialog();
      toast.success('Product created successfully');
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error(error.response?.data?.message || 'Failed to create product');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setProductTab(0);
    setNewProduct({
      name: '',
      productType: 'goods',
      canBeSold: true,
      canBePurchased: true,
      isRental: false,
      trackInventory: false,
      invoicingPolicy: 'ordered',
      salesPrice: '',
      salesTax: '5',
      cost: '',
      purchaseTax: '5',
      category: '',
      reference: '',
      barcode: '',
      hsnCode: '',
      company: 'all',
      internalNotes: '',
    });
  };

  const selectedProduct = products.find(p => p._id === value) || null;

  // Pre-compute filtered options based on inputValue.
  // This ensures the options array reference changes on every search,
  // forcing MUI Autocomplete to fully re-render the listbox (no stale items).
  const filteredOptions = useMemo(() => {
    const searchTerm = (inputValue || '').toLowerCase().trim();
    
    const addNewOption = {
      isAddNew: true,
      label: searchTerm
        ? `+ Create "${inputValue}"`
        : `+ Create "New Product"`,
      inputValue: inputValue,
    };
    
    if (!searchTerm) {
      return [addNewOption];
    }
    
    const prefixMatches = [];
    const containsMatches = [];
    
    products.forEach(option => {
      const cleanName = String(cleanProductName(option.name || '')).toLowerCase();
      if (!cleanName) return;
      
      if (cleanName.startsWith(searchTerm)) {
        prefixMatches.push(option);
      } else if (
        cleanName.includes(searchTerm) ||
        String(option.category || '').toLowerCase().includes(searchTerm) ||
        String(option.sku || '').toLowerCase().includes(searchTerm) ||
        String(option.reference || '').toLowerCase().includes(searchTerm) ||
        String(option.hsnCode || '').toLowerCase().includes(searchTerm) ||
        String(option.barcode || '').toLowerCase().includes(searchTerm)
      ) {
        containsMatches.push(option);
      }
    });
    
    return [...prefixMatches, ...containsMatches, addNewOption];
  }, [products, inputValue]);

  return (
    <>
      <Autocomplete
        size="small"
        options={filteredOptions}
        value={selectedProduct}
        inputValue={inputValue}
        open={open && inputValue.length > 0}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onInputChange={(event, newInputValue, reason) => {
          setInputValue(newInputValue);
          // Open dropdown when user types, close when empty
          if (reason === 'input') {
            setOpen(newInputValue.length > 0);
          }
          // Clear selection when user starts typing a new search
          if (reason === 'input' && selectedProduct && newInputValue) {
            onChange('');
          }
        }}
        onChange={(e, newValue) => {
          if (newValue && newValue.isAddNew) {
            setDialogOpen(true);
          } else {
            if (onProductSelect && newValue) {
              onProductSelect(newValue);
            }
            onChange(newValue ? newValue._id : '');
            setInputValue('');
          }
        }}
        loading={loading}
        getOptionLabel={(option) => {
          if (option.isAddNew) return option.label;
          return cleanProductName(option?.name || '');
        }}
        getOptionKey={(option) => {
          if (option.isAddNew) return `add-new-${option.inputValue || 'empty'}`;
          return option._id || option.id || option.sku || cleanProductName(option.name);
        }}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          if (option.isAddNew || val.isAddNew) return false;
          return option._id === val._id;
        }}
        filterOptions={(opts) => opts}
        noOptionsText="No products found"
        renderOption={(props, option) => {
          // CRITICAL: Extract key from props (React 18 requirement)
          // Otherwise React can't properly reconcile DOM and stale items persist
          const { key, ...restProps } = props;
          if (option.isAddNew) {
            return (
              <Box
                key={key}
                component="li"
                {...restProps}
                sx={{ 
                  color: '#875A7B', 
                  fontWeight: 500,
                  '&:hover': { backgroundColor: 'rgba(135, 90, 123, 0.08)' }
                }}
              >
                <Add sx={{ mr: 1, fontSize: 18 }} />
                {option.label}
              </Box>
            );
          }
          return (
            <Box key={key} component="li" {...restProps} sx={{ whiteSpace: 'normal', overflow: 'visible' }}>
              {cleanProductName(option.name)}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            placeholder="Type product name to search..."
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
            }}
            sx={{ borderBottom: '1px solid #e0e0e0' }}
          />
        )}
        slotProps={{
          paper: {
            sx: {
              maxHeight: '300px',
              '& .MuiAutocomplete-option': {
                whiteSpace: 'normal',
                height: 'auto',
                padding: '8px 12px',
              },
            },
          },
        }}
      />

      {/* Add Product Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '4px',
            minHeight: '80vh',
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          py: 1.5,
          px: 2,
          backgroundColor: '#f8f8f8',
        }}>
          <Typography sx={{ fontWeight: 500, fontSize: '16px', color: '#333' }}>
            Create Product
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" sx={{ color: '#666' }}>
              <OpenInFull sx={{ fontSize: '18px' }} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={handleCloseDialog}
              sx={{ color: '#666' }}
            >
              <Close sx={{ fontSize: '18px' }} />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Main Form Area */}
          <Box sx={{ p: 3, flex: 1 }}>
            {/* Product Label + Name + Image */}
            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '12px', color: '#666', mb: 0.5 }}>Product</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" sx={{ color: '#999' }}>
                    <StarBorder sx={{ fontSize: '24px' }} />
                  </IconButton>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    placeholder="e.g. DELL XPS 13 Laptop"
                    InputProps={{
                      disableUnderline: true,
                      sx: { 
                        fontSize: '28px', 
                        fontWeight: 400,
                        color: '#333',
                        borderBottom: '1px solid #e0e0e0',
                        pb: 0.5,
                      }
                    }}
                  />
                </Box>
              </Box>
              
              {/* Product Image */}
              <Box sx={{ 
                width: 80, 
                height: 80, 
                backgroundColor: '#e8f4f4', 
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#d8eeee' }
              }}>
                <AddPhotoAlternate sx={{ fontSize: 40, color: '#00a09d' }} />
              </Box>
            </Box>
            
            {/* Sales / Purchase / Rental Checkboxes */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={newProduct.canBeSold} 
                    onChange={(e) => setNewProduct({ ...newProduct, canBeSold: e.target.checked })}
                    size="small"
                    sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }}
                  />
                }
                label={<Typography sx={{ fontSize: '13px' }}>Sales</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={newProduct.canBePurchased} 
                    onChange={(e) => setNewProduct({ ...newProduct, canBePurchased: e.target.checked })}
                    size="small"
                    sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }}
                  />
                }
                label={<Typography sx={{ fontSize: '13px' }}>Purchase</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={newProduct.isRental} 
                    onChange={(e) => setNewProduct({ ...newProduct, isRental: e.target.checked })}
                    size="small"
                    sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '13px' }}>Rental</Typography>
                    <Tooltip title="Enable rental for this product">
                      <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                    </Tooltip>
                  </Box>
                }
              />
            </Box>
            
            {/* Tabs */}
            <Tabs
              value={productTab}
              onChange={(e, newValue) => setProductTab(newValue)}
              sx={{
                minHeight: '36px',
                borderBottom: '1px solid #e0e0e0',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '13px',
                  minHeight: '36px',
                  py: 0.5,
                  px: 2,
                  color: '#666',
                  '&.Mui-selected': { color: '#333', fontWeight: 500 },
                },
                '& .MuiTabs-indicator': { backgroundColor: '#333' },
              }}
            >
              <Tab label="General Information" />
              <Tab label="Sales" />
              <Tab label="Purchase" />
              <Tab label="Inventory" />
            </Tabs>
            
            {/* Tab Content */}
            <Box sx={{ pt: 2 }}>
              {productTab === 0 && (
                <Grid container spacing={4}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    {/* Product Type */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Product Type</Typography>
                        <Tooltip title="Type of the product">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <RadioGroup
                        row
                        value={newProduct.productType}
                        onChange={(e) => setNewProduct({ ...newProduct, productType: e.target.value })}
                      >
                        <FormControlLabel 
                          value="goods" 
                          control={<Radio size="small" sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }} />} 
                          label={<Typography sx={{ fontSize: '13px' }}>Goods</Typography>}
                        />
                        <FormControlLabel 
                          value="service" 
                          control={<Radio size="small" sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }} />} 
                          label={<Typography sx={{ fontSize: '13px' }}>Service</Typography>}
                        />
                        <FormControlLabel 
                          value="combo" 
                          control={<Radio size="small" sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }} />} 
                          label={<Typography sx={{ fontSize: '13px' }}>Combo</Typography>}
                        />
                      </RadioGroup>
                    </Box>
                    
                    {/* Track Inventory */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Track Inventory</Typography>
                        <Tooltip title="Track stock levels for this product">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <Checkbox 
                        checked={newProduct.trackInventory} 
                        onChange={(e) => setNewProduct({ ...newProduct, trackInventory: e.target.checked })}
                        size="small"
                        sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' }, p: 0 }}
                      />
                    </Box>
                    
                    {/* Invoicing Policy */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Invoicing Policy</Typography>
                        <Tooltip title="Quantities invoiced: Ordered or Delivered">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <Typography sx={{ fontSize: '13px', color: '#666' }}>Ordered quantities</Typography>
                    </Box>
                  </Grid>
                  
                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    {/* Sales Price */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Sales Price</Typography>
                        <Tooltip title="Price at which the product is sold">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <TextField
                        variant="standard"
                        value={newProduct.salesPrice}
                        onChange={(e) => setNewProduct({ ...newProduct, salesPrice: e.target.value })}
                        placeholder="₹ 0.00"
                        type="number"
                        InputProps={{
                          disableUnderline: true,
                          startAdornment: <InputAdornment position="start" sx={{ mr: 0.5 }}>₹</InputAdornment>,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                        sx={{ width: '150px' }}
                      />
                    </Box>
                    
                    {/* Sales Taxes */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Sales Taxes</Typography>
                        <Tooltip title="Default taxes for sales">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <Chip 
                        label={`${newProduct.salesTax}% GST`} 
                        onDelete={() => setNewProduct({ ...newProduct, salesTax: '0' })}
                        size="small"
                        sx={{ backgroundColor: '#e8e8e8', fontSize: '12px' }}
                      />
                    </Box>
                    
                    {/* Cost */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Cost</Typography>
                        <Tooltip title="Cost price of the product">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <TextField
                        variant="standard"
                        value={newProduct.cost}
                        onChange={(e) => setNewProduct({ ...newProduct, cost: e.target.value })}
                        placeholder="₹ 0.00"
                        type="number"
                        InputProps={{
                          disableUnderline: true,
                          startAdornment: <InputAdornment position="start" sx={{ mr: 0.5 }}>₹</InputAdornment>,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                        sx={{ width: '150px' }}
                      />
                    </Box>
                    
                    {/* Purchase Taxes */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Purchase Taxes</Typography>
                        <Tooltip title="Default taxes for purchases">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <Chip 
                        label={`${newProduct.purchaseTax}% GST`} 
                        onDelete={() => setNewProduct({ ...newProduct, purchaseTax: '0' })}
                        size="small"
                        sx={{ backgroundColor: '#e8e8e8', fontSize: '12px' }}
                      />
                    </Box>
                    
                    {/* Category */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Typography sx={{ width: '130px', fontSize: '13px', color: '#333' }}>Category</Typography>
                      <TextField
                        select
                        variant="standard"
                        value={newProduct.category}
                        onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                        sx={{ flex: 1, minWidth: 150 }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (value) => value ? {
                            hardware: 'Hardware',
                            software: 'Software',
                            service: 'Services',
                            subscription: 'Subscriptions',
                            consulting: 'Consulting',
                            support: 'Support',
                            training: 'Training',
                            other: 'Other'
                          }[value] || value : <span style={{ color: '#999' }}>Select category...</span>
                        }}
                      >
                        <MenuItem value="hardware">Hardware</MenuItem>
                        <MenuItem value="software">Software</MenuItem>
                        <MenuItem value="service">Services</MenuItem>
                        <MenuItem value="subscription">Subscriptions</MenuItem>
                        <MenuItem value="consulting">Consulting</MenuItem>
                        <MenuItem value="support">Support</MenuItem>
                        <MenuItem value="training">Training</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </TextField>
                    </Box>
                    
                    {/* Reference */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Typography sx={{ width: '130px', fontSize: '13px', color: '#333' }}>Reference</Typography>
                      <TextField
                        variant="standard"
                        value={newProduct.reference}
                        onChange={(e) => setNewProduct({ ...newProduct, reference: e.target.value })}
                        placeholder="Internal reference..."
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    
                    {/* Barcode */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Typography sx={{ width: '130px', fontSize: '13px', color: '#333' }}>Barcode</Typography>
                      <TextField
                        variant="standard"
                        value={newProduct.barcode}
                        onChange={(e) => setNewProduct({ ...newProduct, barcode: e.target.value })}
                        placeholder="Barcode..."
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    
                    {/* HSN/SAC Code */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>HSN/SAC Code</Typography>
                        <Tooltip title="Harmonized System of Nomenclature / Service Accounting Code">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <TextField
                        variant="standard"
                        value={newProduct.hsnCode}
                        onChange={(e) => setNewProduct({ ...newProduct, hsnCode: e.target.value })}
                        placeholder="HSN/SAC Code..."
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    
                    {/* Company */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Typography sx={{ width: '130px', fontSize: '13px', color: '#333' }}>Company</Typography>
                      <Typography sx={{ fontSize: '13px', color: '#999' }}>Visible to all</Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
              
              {productTab === 1 && (
                <Typography sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                  Sales settings will be available after creating the product.
                </Typography>
              )}
              
              {productTab === 2 && (
                <Typography sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                  Purchase settings will be available after creating the product.
                </Typography>
              )}
              
              {productTab === 3 && (
                <Typography sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                  Inventory settings will be available after creating the product.
                </Typography>
              )}
            </Box>
          </Box>
          
          {/* Internal Notes Section */}
          <Box sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#333', textTransform: 'uppercase' }}>
                Internal Notes
              </Typography>
              <IconButton size="small" sx={{ ml: 'auto', color: '#666' }}>
                <ExpandMore sx={{ fontSize: '18px' }} />
              </IconButton>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              value={newProduct.internalNotes}
              onChange={(e) => setNewProduct({ ...newProduct, internalNotes: e.target.value })}
              placeholder="Internal notes..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '13px',
                  '& fieldset': { borderColor: '#e0e0e0' },
                }
              }}
            />
          </Box>
        </DialogContent>
        
        {/* Footer */}
        <DialogActions sx={{ 
          px: 2, 
          py: 1.5, 
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f8f8',
          justifyContent: 'flex-start',
        }}>
          <Button
            variant="contained"
            onClick={handleAddProduct}
            sx={{
              backgroundColor: '#00a09d',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#008f8c' },
            }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            sx={{
              backgroundColor: '#4a4a4a',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#333' },
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductSelect;
